import { useNavigate } from "react-router-dom";
import { Card, Tag, StatusBadge } from "@/components/ui-kit";
import { fileUrl } from "@/api";
import { useAuth } from "@/auth";
import { Calendar, Check, Percent, Link2, ArrowRight, Infinity as InfinityIcon } from "lucide-react";
import { affiliateValidity, formatCommission, affiliateTheme } from "@/lib/affiliate";

export function AffiliateBadge({ c, className = "" }) {
  const t = affiliateTheme(c);
  return (
    <span data-testid="affiliate-badge" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-[0.08em] ${t.badge} ${className}`}>
      <Link2 size={12} strokeWidth={2.5} /> AFFILIATE
    </span>
  );
}

function Logo({ c }) {
  return (
    <div className="h-14 w-14 rounded-full bg-white border border-white/80 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
      {c.business_logo
        ? <img src={fileUrl(c.business_logo)} alt="" className="h-full w-full object-contain p-1.5" />
        : <span className="text-lg font-semibold text-[#292827]">{(c.business_name || "?").slice(0, 1).toUpperCase()}</span>}
    </div>
  );
}

export function AffiliateCard({ c, onClick }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = affiliateTheme(c);
  const cats = c.categories || [];
  const cover = c.images?.[0];
  const open = onClick || (user?.role === "creator" ? () => navigate(`/dashboard/kampanjat/${c.campaign_id}`) : undefined);
  const openEnded = !c.end_date;
  return (
    <Card onClick={open} data-testid="affiliate-card"
      className={`group flex flex-col overflow-hidden rounded-3xl soft-shadow cursor-pointer transition-[transform,box-shadow] duration-300 ease-out hover:soft-shadow-hover hover:-translate-y-1 ${t.card}`}>
      {cover && (
        <div className="h-48 w-full overflow-hidden bg-[#E9EAEC]">
          <img src={fileUrl(cover)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo c={c} />
            <div className="min-w-0">
              <p className="truncate text-[14px] text-[#3A3733]" data-testid="affiliate-card-company">{c.business_name}</p>
              <AffiliateBadge c={c} className="mt-1" />
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-white"><StatusBadge status={c.is_expired ? "expired" : c.status} /></span>
        </div>
        <h4 className="mt-5 text-lg md:text-xl font-semibold tracking-display text-[#292827] leading-snug break-words" data-testid="affiliate-card-title">{c.title}</h4>
        {cats.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{cats.slice(0, 3).map((x) => <Tag key={x}>{x}</Tag>)}</div>}
        <div className={`mt-4 rounded-2xl px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[#3A3733] ${t.strip}`}>
          <span className={`flex items-center gap-1.5 font-medium ${t.accentText}`} data-testid="affiliate-card-commission">
            <span className={`h-5 w-5 rounded-full flex items-center justify-center ${t.icon}`}><Percent size={11} strokeWidth={3} /></span>
            {formatCommission(c.commission)} provisio
          </span>
          <span className="flex items-center gap-1.5" data-testid="affiliate-card-validity">
            {openEnded ? <InfinityIcon size={15} /> : <Calendar size={14} />} {affiliateValidity(c)}
          </span>
        </div>
        <p className="mt-4 text-[15px] text-[#3A3733] leading-relaxed line-clamp-3" data-testid="affiliate-card-description">{c.description}</p>
        <span className="mt-2 text-[13px] font-medium text-[#5B7FB0]">Lue lisää</span>
        {user?.role === "creator" && c.has_applied && (
          <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6E675C]" data-testid="affiliate-card-applied"><Check size={14} /> Hakemus lähetetty</span>
        )}
        <span className="mt-auto pt-5 block" data-testid="affiliate-card-open">
          <span className={`flex items-center justify-center gap-2 w-full rounded-full px-5 py-3.5 text-[15px] font-semibold transition-colors ${t.button}`}>
            Tutustu ohjelmaan <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </span>
      </div>
    </Card>
  );
}

export function AffiliateSummary({ c }) {
  const t = affiliateTheme(c);
  return (
    <Card className={`mt-6 p-6 ${t.card}`} data-testid="application-affiliate-info">
      <AffiliateBadge c={c} />
      {c.description && <p className="mt-4 text-[15px] text-[#3A3733] leading-relaxed line-clamp-4">{c.description}</p>}
      <div className={`mt-5 rounded-2xl px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#292827] ${t.strip}`}>
        <span className={`flex items-center gap-1.5 font-medium ${t.accentText}`}><Percent size={15} /> {formatCommission(c.commission)} provisio</span>
        <span className="flex items-center gap-1.5"><Calendar size={15} /> {affiliateValidity(c)}</span>
        <span>{c.who_can_join === "all" ? "Kaikki voivat liittyä" : "Yritys valitsee hakijat"}</span>
      </div>
      {c.categories?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{c.categories.map((x) => <Tag key={x}>{x}</Tag>)}</div>}
    </Card>
  );
}
